import * as React from 'react';
import PropTypes from 'prop-types';
import {
    Typography, Modal, IconButton, makeStyles,
} from '@material-ui/core';
import classnames from 'classnames';
import { Fullscreen } from '@material-ui/icons';

import { Translate } from 'react-localize-redux';
import {
    CloseIcon,
} from '../icons';

import { isIe } from '../../youwell-common/browserUtils';

const defaultWidth = 568;

const useStyles = makeStyles(theme => ({
    root: {
        // z-index is set inline by Modal-component. But must sometimes be overridden by using !important
    },
    modalContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: defaultWidth,
        maxWidth: '100%',
        minHeight: theme.spacing(50),
        maxHeight: '80%',
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        height: isIe ? '100%' : undefined,

        [theme.breakpoints.down('xs')]: {
            minWidth: 0,
            width: `calc(100% - ${theme.spacing(4)}px)`,
        },
    },
    wide: {
        minWidth: '80%',
    },
    fullscreen: {
        top: 0,
        left: 0,
        transform: 'none',
        width: '100vw',
        height: '100vh',
        maxHeight: '100%',
    },
    modalHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        background: theme.youwell.colors.secondaryBackground,
    },
    modalHeaderText: {
        flex: '1 1 auto',
    },
    headerMessage: {
        marginRight: theme.spacing(1),
    },
    modalContent: {
        flex: '1 1 auto',
        padding: theme.spacing(2),
        overflowY: 'auto',

        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2, 1),
        },
    },
}));

const PortalModal = ({
    heading, children, open, onClose, wide, showUnsavedMessage, allowZoom, fixedWidth, fixedHeight,
}) => {
    const classes = useStyles();
    const [fullscreen, setFullscreen] = React.useState(false);

    const closeModal = () => {
        setFullscreen(false);
        onClose();
    }

    return (
        <Modal
            aria-labelledby="modal-window"
            open={open}
            onClose={closeModal}
            className={classes.root}
        >
            <div className={classnames(classes.modalContainer, { [classes.wide]: wide, [classes.fullscreen]: fullscreen })} style={{ width: fixedWidth, height: fixedHeight }}>
                {heading && (
                    <div className={classes.modalHeader}>
                        <Typography variant="h6" className={classes.modalHeaderText} id="modal-window">
                            {heading}
                        </Typography>
                        {showUnsavedMessage && (
                            <Typography color="error" className={classes.headerMessage}>
                                <Translate id="youHaveUnsavedChanges_SaveOrAbort" />
                            </Typography>
                        )}
                        {allowZoom && (
                            <IconButton onClick={() => setFullscreen(prev => !prev)}>
                                <Fullscreen />
                            </IconButton>
                        )}
                        <IconButton onClick={closeModal}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                )}
                <div className={classes.modalContent}>
                    {open && children}
                </div>
            </div>
        </Modal>
    );
};

PortalModal.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
    heading: PropTypes.node,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    wide: PropTypes.bool,
    showUnsavedMessage: PropTypes.bool,
    allowZoom: PropTypes.bool,
    fixedWidth: PropTypes.number,
    fixedHeight: PropTypes.number,
};

PortalModal.defaultProps = {
    children: null,
    heading: null,
    wide: false,
    showUnsavedMessage: false,
    allowZoom: false,
    fixedWidth: null,
    fixedHeight: null,
};

export default PortalModal;
